/**
 * リトライ機能を提供するユーティリティ関数
 */

/**
 * 指定された回数までリトライを実行する
 * @param {Function} fn - 実行する関数
 * @param {number} maxRetries - 最大リトライ回数
 * @param {number} delay - リトライ間隔（ミリ秒）
 * @param {Function} shouldRetry - リトライするかどうかを判定する関数
 * @returns {Promise} 実行結果
 */
export const retryWithBackoff = async (fn, maxRetries = 3, delay = 1000, shouldRetry = null) => {
    let lastError;

    for (let attempt = 0; attempt <= maxRetries; attempt++) {
        try {
            return await fn();
        } catch (error) {
            lastError = error;

            // 最後の試行の場合はエラーを投げる
            if (attempt === maxRetries) {
                throw error;
            }

            // shouldRetry関数が指定されている場合は、それに基づいてリトライを判定
            if (shouldRetry && !shouldRetry(error)) {
                throw error;
            }

            // 指数バックオフで待機時間を計算
            const waitTime = delay * Math.pow(2, attempt);
            console.log(`Attempt ${attempt + 1} failed, retrying in ${waitTime}ms...`, error.message);

            await new Promise(resolve => setTimeout(resolve, waitTime));
        }
    }

    throw lastError;
};

/**
 * ネットワークエラーかどうかを判定する
 * @param {Error} error - エラーオブジェクト
 * @returns {boolean} ネットワークエラーの場合true
 */
export const isNetworkError = (error) => {
    if (!error) return false;

    // ネットワーク関連のエラーコード
    const networkErrorCodes = [
        'NETWORK_ERROR',
        'TIMEOUT',
        'CONNECTION_ERROR',
        'ECONNRESET',
        'ENOTFOUND',
        'ECONNREFUSED'
    ];

    // HTTPステータスコード
    const networkStatusCodes = [408, 429, 500, 502, 503, 504];

    return (
        networkErrorCodes.some(code => error.code === code) ||
        networkStatusCodes.includes(error.response?.status) ||
        error.message?.toLowerCase().includes('network') ||
        error.message?.toLowerCase().includes('timeout') ||
        error.message?.toLowerCase().includes('connection')
    );
};

/**
 * 一時的なエラーかどうかを判定する
 * @param {Error} error - エラーオブジェクト
 * @returns {boolean} 一時的なエラーの場合true
 */
export const isTemporaryError = (error) => {
    if (!error) return false;

    // 一時的なエラーのHTTPステータスコード
    const temporaryStatusCodes = [408, 429, 500, 502, 503, 504];

    return (
        isNetworkError(error) ||
        temporaryStatusCodes.includes(error.response?.status) ||
        error.response?.status >= 500
    );
};

/**
 * アップロード用のリトライ関数
 * @param {Function} uploadFn - アップロード関数
 * @param {Object} options - オプション
 * @returns {Promise} アップロード結果
 */
export const retryUpload = async (uploadFn, options = {}) => {
    const {
        maxRetries = 3,
        baseDelay = 1000,
        shouldRetry = isTemporaryError
    } = options;

    return retryWithBackoff(uploadFn, maxRetries, baseDelay, shouldRetry);
};

/**
 * API呼び出し用のリトライ関数
 * @param {Function} apiFn - API関数
 * @param {Object} options - オプション
 * @returns {Promise} API結果
 */
export const retryApiCall = async (apiFn, options = {}) => {
    const {
        maxRetries = 2,
        baseDelay = 500,
        shouldRetry = isTemporaryError
    } = options;

    return retryWithBackoff(apiFn, maxRetries, baseDelay, shouldRetry);
};

